import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Percent, Loader2, Edit3, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";

const fmtBRL = (v) => `R$ ${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

export default function HighInterestPage() {
  const [minPct, setMinPct] = useState(100);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // contract_id em edição
  const [draftPct, setDraftPct] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/contracts/high-interest?min_percent=${minPct}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar lista");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startEdit = (ct) => {
    setEditing(ct.contract_id);
    setDraftPct(String(ct.interest_percent));
    setReason("");
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraftPct("");
    setReason("");
  };

  const save = async (ct) => {
    const newPct = Number(draftPct);
    if (Number.isNaN(newPct) || newPct < 0 || newPct > 999) {
      toast.error("Percentual inválido (0–999)");
      return;
    }
    if (!reason || reason.trim().length < 3) {
      toast.error("Motivo é obrigatório (mín. 3 caracteres)");
      return;
    }
    setSaving(true);
    try {
      const { data: out } = await api.patch(`/admin/contracts/${ct.contract_id}/interest-percent`, {
        new_percent: newPct,
        reason: reason.trim(),
      });
      if (out.changed) {
        toast.success(`Juros atualizado: ${ct.interest_percent.toFixed(2)}% → ${newPct.toFixed(2)}%`);
        // update local state
        setData((d) => ({
          ...d,
          contracts: d.contracts.map((c) =>
            c.contract_id === ct.contract_id ? { ...c, interest_percent: newPct } : c
          ),
        }));
      } else {
        toast.info("Sem alteração — valor já era esse");
      }
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Administração</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 flex items-center gap-2" data-testid="high-interest-title">
          <Percent size={22} className="text-[#D4AF37]"/> Edição rápida de juros
        </h1>
        <p className="text-[#A3A3A3] text-[13px] sm:text-sm mt-1.5 max-w-2xl">
          Lista contratos normais ativos com percentual de juros acima do threshold (default 100%). Edita o <span className="text-[#E8C547]">interest_percent</span> e recalcula o <span className="text-[#E8C547]">interest_amount</span> do ciclo aberto.
        </p>
      </div>

      <div className="fc-card p-4 mb-4 border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0"/>
          <div className="text-[12.5px] text-[#A3A3A3] leading-relaxed">
            <div className="text-[#E5E5E5] font-medium mb-1">Como funciona</div>
            • Edição afeta apenas contratos <strong>normais ATIVOS</strong> (não parcelados/acordos)<br/>
            • Pagamentos já realizados <strong>NÃO são alterados</strong> — só o cálculo do ciclo aberto e dos próximos<br/>
            • Toda alteração é gravada em <code className="text-[11px] bg-[#0a0a0a] px-1 py-0.5 rounded">contract_history</code> com motivo e usuário<br/>
            • Dashboards (Executivo, Financeiro, Sócios, Tesouraria) recalculam automaticamente — basta recarregar
          </div>
        </div>
      </div>

      <div className="flex items-end gap-3 mb-5">
        <div>
          <label className="text-[11px] text-[#A3A3A3] uppercase tracking-wide block mb-1">Mín. juros (%)</label>
          <input
            type="number" min="0" step="1" value={minPct}
            onChange={(e) => setMinPct(Number(e.target.value))}
            className="fc-input w-32"
            data-testid="min-percent-input"
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 rounded-md bg-[#D4AF37] text-black font-medium text-[13px] flex items-center gap-2 disabled:opacity-40"
          data-testid="recarregar-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin"/> : <Percent size={14}/>}
          Filtrar
        </button>
        {data && (
          <div className="ml-2 text-[12.5px] text-[#A3A3A3]">
            <span className="font-mono-tab text-[#E5E5E5]">{data.total}</span> contrato(s) com juros ≥ <span className="font-mono-tab text-[#E5E5E5]">{data.min_percent}%</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="fc-card p-8 flex items-center justify-center gap-2 text-[#A3A3A3]">
          <Loader2 className="animate-spin" size={16}/> Carregando...
        </div>
      )}

      {!loading && data && (
        <div className="fc-card p-0 overflow-hidden" data-testid="high-interest-table">
          {data.contracts.length === 0 ? (
            <div className="px-4 py-10 text-center text-[#737373] text-[13px]">
              Nenhum contrato encontrado com juros ≥ {data.min_percent}% — Tudo certo!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#0f0f0f] text-[#737373] uppercase text-[10.5px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Cliente</th>
                    <th className="text-left px-4 py-2 font-medium">Sócio</th>
                    <th className="text-left px-4 py-2 font-medium">Indicador</th>
                    <th className="text-right px-4 py-2 font-medium">Capital</th>
                    <th className="text-right px-4 py-2 font-medium">Juros atual</th>
                    <th className="text-left px-4 py-2 font-medium">Criado em</th>
                    <th className="text-right px-4 py-2 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {data.contracts.map((ct) => {
                    const isEditing = editing === ct.contract_id;
                    return (
                      <tr key={ct.contract_id} className="border-t border-[#1a1a1a] hover:bg-[#0e0e0e]">
                        <td className="px-4 py-2.5">
                          <div className="text-[#E5E5E5]">{ct.client_name || "—"}</div>
                          {ct.client_cpf && <div className="text-[10.5px] text-[#737373] font-mono-tab">{ct.client_cpf}</div>}
                          {ct.imported_from_xlsx && (
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9.5px] bg-blue-500/10 border border-blue-500/30 text-blue-300">
                              importado
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-[#A3A3A3]">{ct.socio_name}</td>
                        <td className="px-4 py-2.5 text-[#A3A3A3]">{ct.indicator_name}</td>
                        <td className="px-4 py-2.5 text-right font-mono-tab text-[#E5E5E5]">{fmtBRL(ct.capital)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <input
                              type="number" step="0.01" min="0" max="999"
                              value={draftPct} onChange={(e) => setDraftPct(e.target.value)}
                              className="fc-input w-24 text-right font-mono-tab"
                              autoFocus
                              data-testid={`edit-pct-${ct.contract_id}`}
                            />
                          ) : (
                            <span className={`font-mono-tab font-medium ${ct.interest_percent >= 200 ? "text-red-300" : "text-amber-300"}`}>
                              {ct.interest_percent.toFixed(2)}%
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-[#737373] text-[12px]">
                          {ct.created_at ? new Date(ct.created_at).toLocaleDateString("pt-BR") : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <div className="flex items-center gap-2 justify-end">
                              <input
                                type="text" placeholder="Motivo (obrigatório)"
                                value={reason} onChange={(e) => setReason(e.target.value)}
                                className="fc-input text-[12px] w-48"
                                data-testid={`reason-${ct.contract_id}`}
                              />
                              <button
                                onClick={() => save(ct)} disabled={saving}
                                className="p-1.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40"
                                data-testid={`save-${ct.contract_id}`}
                              >
                                {saving ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-1.5 rounded bg-[#1a1a1a] border border-[#2a2a2a] text-[#A3A3A3] hover:border-[#D4AF37]"
                                data-testid={`cancel-${ct.contract_id}`}
                              >
                                <X size={13}/>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(ct)}
                              className="px-2 py-1 rounded text-[12px] bg-[#1a1a1a] border border-[#2a2a2a] text-[#E5E5E5] hover:border-[#D4AF37] flex items-center gap-1 ml-auto"
                              data-testid={`edit-${ct.contract_id}`}
                            >
                              <Edit3 size={11}/> Editar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
